import React, { useEffect, useRef, useState } from "react";
import ModalYappyBtn from "../../Yappy/ModalYappyBtn";
import ModalStatusPayment from "../ModalStatusPayment";
import ErrorAlert from "../../Alerts/ErrorAlert";
import { TableSessionsFetchAPI } from "../../../api/TableSessions";
import { EvertechFetchAPI } from "../../../api/Evertech";
import { PowertranzFetchAPI } from "../../../api/Powertranz1";
import { YappyFetchAPI } from "../../../api/PaymentPost/Yappy";
import { OrderNumberFetchAPI } from "../../../api/PaymentPost/OrderNumber";
import { generateRandomString } from "../../../utils/RandomString";
import Socket from "../../../utils/Socket.config";

/*
 * TableSessionPayment - isolated payment module for closing a table's account from the customer's
 * phone once the waiter has authorized it (session.payment_authorized - see Checkout.jsx's Pagar
 * button). Ports the gateway orchestration already proven in Checkout.jsx (Yappy/PowerTranz/Evertec)
 * to a fully separate code path:
 *   - Never imports from or mutates Checkout.jsx's own payment state/functions.
 *   - Calls the exact same gateway endpoints the cart checkout already uses (POST /evertech,
 *     /powertranz, /powertranz/payment, /btn/yappy via EvertechFetchAPI/PowertranzFetchAPI/
 *     YappyFetchAPI) - no table-specific backend wrappers, those endpoints already work as-is with
 *     the ecommerce public token the browser already carries.
 *   - The amount sent to each gateway is the table's own server-computed total (order.totals, read
 *     fresh from the session/order snapshot) rather than a calculation_id - simpler than the cart
 *     flow's anti-tampering "official calculation" system, at the cost of trusting whatever this
 *     component currently holds in memory for the total.
 *   - On gateway approval, calls checkoutTableSession directly (creates the final Order, closes the
 *     table session, and deletes the TempOrder in one shot via controller.newOrder's existing
 *     pre_order_code handling). This intentionally skips the cart checkout's "pending manager
 *     approval" detour (registerPendingApprovalOrder) - that queue exists for delivery/pickup
 *     fraud review and doesn't apply to a customer paying at their own table.
 *
 * NEVER TESTED AGAINST LIVE/SANDBOX GATEWAY CREDENTIALS. This was ported from working code by
 * reading it line by line, not re-derived from scratch, but it still needs real staging runs
 * against each gateway (Yappy, PowerTranz, Evertec) before going anywhere near real transactions.
 */

const normalizePaymentText = (value) => String(value || "").trim().toLowerCase();
const getPaymentIntegrationId = (payment = {}) => Number(payment?.integration?.id ?? payment?.integration_id ?? 0);
const getPaymentIntegrationName = (payment = {}) => normalizePaymentText(payment?.integration?.name ?? payment?.integration_name ?? "");
const getPaymentName = (payment = {}) => normalizePaymentText(payment?.payment ?? "");

const isYappyPayment = (payment = {}) => {
  const name = getPaymentName(payment);
  const integrationName = getPaymentIntegrationName(payment);
  const integrationId = getPaymentIntegrationId(payment);
  return name.includes("yappy") || (integrationId === 2 && integrationName.includes("banco general"));
};

const isEvertecPayment = (payment = {}) => {
  const name = getPaymentName(payment);
  const integrationName = getPaymentIntegrationName(payment);
  const integrationId = getPaymentIntegrationId(payment);
  return (
    name.includes("evertech") ||
    name.includes("evertec") ||
    (integrationId === 5 && integrationName.includes("pasarela") && !name.includes("powertranz"))
  );
};

const isPowerTranzPayment = (payment = {}) => getPaymentName(payment).includes("powertranz");
const isTilopayPayment = (payment = {}) => getPaymentName(payment).includes("tilopay");

// "Pagar con POS": the customer asks the waiter to charge the claimed lines on the physical terminal
// (BAC/Banesco/KinPOS via smartpay-pos) instead of an online gateway. Checkout.jsx injects this
// synthetic method (payment_id "POS_PHYSICAL") only into the prepay partial-payment modal - see
// Checkout.jsx's PaymentStep `methods` prop.
const isPosPayment = (payment = {}) => payment?.payment_id === "POS_PHYSICAL" || getPaymentName(payment).includes("pagar con pos");

const getGatewayStatus = (data = {}) => String(
  data?.status?.status ?? data?.payment_status ?? data?.status ?? ""
).toUpperCase();

// Coerces an unpredictable gateway error shape (string, {message}, {status}, or nothing at all)
// into plain text. Without this, a payload like the backend's {"error":{"status":400}} ends up
// as paymentApproval.description, and ModalStatusPayment renders it verbatim as `{description}` -
// React refuses to render a plain object as a child and crashes the whole tree to a blank white
// screen instead of showing the customer a message and letting them retry from the table cart.
const toSafeMessage = (value, fallback) => {
  if (typeof value === "string" && value.trim()) return value;
  if (value && typeof value === "object") {
    if (typeof value.message === "string" && value.message.trim()) return value.message;
    if (typeof value.error === "string" && value.error.trim()) return value.error;
    if (value.status !== undefined) return `Error ${value.status} al procesar el pago`;
  }
  return fallback;
};

const getGatewayStatusMessage = (data = {}, fallback = "No se pudo procesar el pago") =>
  toSafeMessage(data?.status?.message ?? data?.message ?? data?.error, fallback);

const getErrorMessage = (err, fallback) =>
  toSafeMessage(err?.response?.data?.error, toSafeMessage(err?.message, fallback));

// Ported verbatim from Checkout.jsx's buildEvertecVoucher - controller.newOrder reads
// EMVStreamResponse.voucher for paymenthod_integration_id===5, so this mutates the same object
// that gets sent back as EMVStreamResponse rather than returning a separate value.
const buildEvertecVoucher = (data = {}) => {
  const payment = Array.isArray(data?.payment) ? data.payment[0] : null;
  const statusDate = String(data?.status?.date || new Date().toISOString());
  const [datePart, timePart = ""] = statusDate.split("T");
  const processorFields = Array.isArray(payment?.processorFields) ? payment.processorFields : [];
  const detailParamsPayment = processorFields
    .map((process) => `${String(process?.keyword || "").toUpperCase()}:                ${process?.value || ""}`)
    .join("\r\n");

  if (!payment) return data?.voucher || "";

  return `\r\n            ***  EVERTEC  ***             \r\n           ${payment?.issuerName || ""}           \r\nREF:                            ${payment?.reference || ""}\r\nDATE: ${datePart}               ${timePart}\r\n\r\nDESCRIPTION:   ${data?.request?.payment?.description || ""}\r\nTOTAL:                         ${payment?.amount?.from?.currency || ""}. ${payment?.amount?.from?.total || ""} \r\n            +++ DETAIL PAYMENT +++            \r\n\r\nPAYMENT METHOD:               ${String(payment?.paymentMethodName || "").toUpperCase()}\r\n${detailParamsPayment}\r\nSTATUS:                       ${data?.status?.status || ""}\r\nRECEIPT:                       ${payment?.receipt || ""}\r\n\r\n                +++ PAYER +++                \r\n\r\nNAME:                        ${data?.request?.payer?.name || ""}\r\nEMAIL:                        ${data?.request?.payer?.email || ""}\nPHONE:                        ${data?.request?.payer?.mobile || ""}\n           ****** FIN  ******            \r\n`;
};

const getYappyEventStatus = (event, detail) => {
  if (event === "create") return "CREATE";
  if (event === "success") return "SUCCESS";

  const eventPayload = event && typeof event === "object" ? event : {};
  const detailPayload = detail && typeof detail === "object" ? detail : {};
  const candidates = [
    typeof event === "string" ? event : "",
    eventPayload?.status,
    eventPayload?.Status,
    eventPayload?.transactionStatus,
    eventPayload?.paymentStatus,
    eventPayload?.code,
    eventPayload?.message,
    detailPayload?.status,
    detailPayload?.Status,
    detailPayload?.transactionStatus,
    detailPayload?.paymentStatus,
    detailPayload?.code,
    detailPayload?.message,
  ];

  const found = candidates.find((value) => value !== null && value !== undefined && value !== "");
  return String(found || "").toUpperCase();
};

const isYappyRejectedStatus = (status) => (
  status.includes("DENIED") ||
  status.includes("REJECT") ||
  status.includes("DECLIN") ||
  status.includes("FAILED") ||
  status.includes("CANCEL")
);

const safeParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (err) {
    return fallback;
  }
};

// TableSessionPayment - props:
//   show: boolean - mounts/unmounts the whole payment attempt (sockets only connect while true).
//   payment: the method object picked in Checkout.jsx's payment-methods modal (PaymentStep shape).
//   order: the collaborative order snapshot (order_code, totals.{subtotal,total,tax,discount}).
//   paymentMode: "POST_PAY" (default) | "PRE_PAY" - see tableSession.payment_mode. PRE_PAY skips the
//     waiter-authorize wait entirely (self-authorizes right before contacting any gateway) and uses
//     a server-verified total instead of `order.totals` - see the dispatch effect below.
//   tableLabel: display-only string ("Mesa 4").
//   customerName / customerEmail: optional, best-effort invoice/receipt display fields.
//   clientPhone: optional, DonLee "Paisanitos" phone the customer linked from the table cart (see
//     Checkout.jsx's Paisanitos chip, DONLEE-only). Forwarded as-is to checkoutTableSession -
//     controller.newOrder already looks this phone up against DonLee/Tapmango and attaches the
//     points/customer automatically when present (same as the POS's own ModalDonlee), so no extra
//     wiring is needed here beyond passing it through. Ignored (no-op) for any other business.
//   lineUuids: optional array of line_uuid strings - pre-pago partial payment mode (Fase 4/2). When
//     given, this claims+prices ONLY those lines (services/tableSessionPayments.js#claimLinesForPayment)
//     instead of locking the whole account, and confirms/releases that one claim instead of ever
//     calling checkoutTableSession - the account stays open, no session close happens. Omit for the
//     existing "pay/close the whole account" behavior (post-pay, or pre-pago's legacy one-shot flow).
//   onClose(): called when the customer cancels/dismisses before any gateway call resolves.
//   onSuccess(result, {partial}): called once the attempt succeeds - result is the final Order for a
//     whole-account checkout, or the updated shared-order snapshot for a partial payment (`partial:
//     true`) - callers must not navigate away on a partial success, the account is still open.
export default function TableSessionPayment({
  show,
  payment,
  order,
  paymentMode,
  tableLabel,
  customerName,
  customerEmail,
  clientPhone,
  lineUuids,
  onClose,
  onSuccess,
}) {
  const isPrepay = paymentMode === "PRE_PAY";
  const isPartialMode = Array.isArray(lineUuids) && lineUuids.length > 0;
  const [paymentApproval, setPaymentApproval] = useState({ status: 0, description: "" });
  const [showModalStatusPayment, setShowModalStatusPayment] = useState(false);
  const [showYappyBtnModal, setShowYappyBtnModal] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const [htmlData, setHtmlData] = useState("");
  const [orderIdPowertranz, setOrderIdPowertranz] = useState("");
  // Pre-pay only: the backend-verified total (and the calculation_id that ties a gateway charge to
  // exactly what checkout will invoice) fetched once in the dispatch effect below, after
  // beginPrepayPayment locks the order so nothing can change between this and the real checkout.
  const [verifiedTotals, setVerifiedTotals] = useState(null);

  const inFlightRef = useRef(false);
  const tabRef = useRef(null);
  const evertecRequestIdRef = useRef(null);
  const powertranzTxRef = useRef(null);
  const startedRef = useRef(false);
  const orderNumberRef = useRef(null);
  const calculationIdRef = useRef(null);
  // Partial payment only (isPartialMode) - the attempt id claimLinesForPayment returns, needed by
  // both confirmPaymentClaim (success) and releasePaymentClaim (failure/cancel).
  const paymentAttemptIdRef = useRef(null);
  // Mirrors verifiedTotals but readable synchronously - the dispatch effect below calls
  // dispatchGateway() (and therefore payWithEvertec/payWithPowerTranz/requestYappyBtnOrder) in the
  // same tick as setVerifiedTotals(...), before React re-renders this closure with the new state,
  // so anything that needs the just-fetched total THIS tick must read the ref, not `totals` below.
  const verifiedTotalsRef = useRef(null);
  const getCurrentTotals = () => (isPrepay && verifiedTotalsRef.current) ? verifiedTotalsRef.current : (order?.totals || {});

  // Display-only (ModalStatusPayment's invoice numbers) - safe to be one render behind, unlike the
  // ref above, since it's only ever read from JSX.
  const totals = (isPrepay && verifiedTotals) ? verifiedTotals : (order?.totals || {});

  const releaseLock = () => {
    inFlightRef.current = false;
  };

  // Best-effort: releases this attempt's claimed lines immediately on failure/cancellation instead of
  // making the customer wait out the backend's own claim TTL (see releaseExpiredClaimsInMemory on the
  // backend, the safety net if this call never lands). No-op outside partial-payment mode.
  const releaseClaimIfNeeded = (reason) => {
    if (!isPartialMode || !paymentAttemptIdRef.current) return;
    const attemptId = paymentAttemptIdRef.current;
    paymentAttemptIdRef.current = null;
    TableSessionsFetchAPI.releasePrepayClaim(attemptId, reason).catch(() => {});
  };

  // Every gateway call below needs a short, human-readable transaction_code - PlaceToPay/Evertec in
  // particular rejects long/composite references ("La informacion del pago es incorrecta
  // (reference)"). This mirrors Checkout.jsx#getOrderNumber exactly (same GET /last_order/:id, same
  // response field) rather than reusing order.order_code (a 32-char Mongo hex id, far too long for
  // that field) or inventing a different format. Cached per payment attempt, not in localStorage -
  // the table flow shouldn't share/clobber the cart checkout's own "orderNumber" key.
  const getOrderNumber = async () => {
    if (orderNumberRef.current) return orderNumberRef.current;

    const branchoffice = safeParse(localStorage.getItem("branchoffice"));
    const response = await OrderNumberFetchAPI.getOrderNumber(branchoffice?.id, undefined);
    const nextOrder = response?.data?.next_transaction_code;
    if (!nextOrder) throw new Error("No se pudo obtener un numero de orden");

    orderNumberRef.current = nextOrder;
    return nextOrder;
  };

  const closeTab = () => {
    if (tabRef.current) {
      try { tabRef.current.close(); } catch (err) { /* already closed */ }
      tabRef.current = null;
    }
  };

  const resetAndClose = () => {
    setShowModalStatusPayment(false);
    setShowYappyBtnModal(false);
    setShowIframe(false);
    setHtmlData("");
    closeTab();
    releaseLock();
  };

  // ---- Shared final step. Whole-account mode: creates the real Order, closes the session, deletes
  // the TempOrder (checkoutTableSession). Partial-payment mode: confirms this ONE claim instead - the
  // account stays open, no session close happens (confirmPaymentClaim). ----
  const finalizeCheckout = async (extraDetails = {}) => {
    setPaymentApproval({ status: 0, description: "Registrando orden..." });
    setShowModalStatusPayment(true);
    try {
      let result;
      if (isPartialMode) {
        if (!paymentAttemptIdRef.current) {
          throw new Error("No se pudo confirmar el pago (falta el intento de pago)");
        }
        result = await TableSessionsFetchAPI.confirmPrepayClaim(paymentAttemptIdRef.current, {
          paymethod_id: payment?.payment_id,
          paymenthod_integration_id: getPaymentIntegrationId(payment),
          gateway_name: payment?.payment || payment?.integration?.name || "",
          ...(calculationIdRef.current ? { calculation_id: calculationIdRef.current } : {}),
          ...extraDetails,
        });
        paymentAttemptIdRef.current = null;
      } else {
        result = await TableSessionsFetchAPI.checkoutTableSession({
          paymethod: payment?.payment_id,
          paymenthod_integration_id: getPaymentIntegrationId(payment),
          client_name: customerName || "Cliente generico",
          client_email: customerEmail || "",
          client_phone: clientPhone || "",
          // Reuses the exact calculation beginPrepayPayment/getPrepayOrderCalculation already priced
          // and showed the gateway - see buildTableCheckoutPayload on the backend, which validates it
          // instead of silently pricing the order again at this point.
          ...(calculationIdRef.current ? { calculation_id: calculationIdRef.current } : {}),
          ...extraDetails,
        });
      }
      setPaymentApproval({
        status: 1,
        description: isPartialMode
          ? "Pago parcial confirmado."
          : (isPrepay ? "Pago confirmado. Tu pedido ya se envio a cocina." : "Cuenta cerrada correctamente"),
      });
      setTimeout(() => {
        resetAndClose();
        onSuccess?.(result, { partial: isPartialMode });
      }, 2000);
    } catch (err) {
      const message = getErrorMessage(err, "No se pudo registrar la orden final. Si el cobro ya se realizo, avisa al mesero antes de reintentar.");
      setPaymentApproval({ status: 2, description: message });
      ErrorAlert("", message, "error");
      releaseLock();
    }
  };

  // ================= Evertec / PlaceToPay =================
  const payWithEvertec = async () => {
    const machine = safeParse(localStorage.getItem("machine"));
    const branchoffice = safeParse(localStorage.getItem("branchoffice"));
    if (!branchoffice?.id || !machine?.id) {
      setPaymentApproval({ status: 2, description: "No se encontro la sucursal o la maquina" });
      setShowModalStatusPayment(true);
      releaseLock();
      return;
    }

    try {
      const orderNumber = await getOrderNumber();
      const randomString = generateRandomString(4);

      const response = await EvertechFetchAPI.newPayment({
        branchoffice_id: branchoffice.id,
        branch_machine_id: machine.id,
        transaction_code: orderNumber,
        evertech_code: orderNumber + randomString,
        description: `Smartpay order ${orderNumber}`,
        total: Number(getCurrentTotals().total) || 0,
        client: {
          name: (customerName || "Cliente generico").split(" ")[0] || "",
          surname: (customerName || "").split(" ").slice(1).join(" ") || "",
          email: customerEmail || "",
        },
      });

      const evertecData = response?.data?.data;
      if (!evertecData?.requestId || !evertecData?.processUrl) {
        throw new Error("Respuesta invalida de Evertec");
      }

      // Evertec's own return redirect can only tell the backend which machine room to notify by
      // looking up a TempOrder via evertech_code (see attachEvertechTracking on the backend) - without
      // this, neither approval nor cancellation would ever reach this screen and it would stay stuck
      // on "Procesando pago..." forever. Must happen before opening the tab.
      await TableSessionsFetchAPI.trackEvertechPayment({
        evertech_code: orderNumber + randomString,
        evertech_transaction_id: evertecData.requestId,
      });

      evertecRequestIdRef.current = evertecData.requestId;

      const tab = window.open(evertecData.processUrl, "_blank");
      if (!tab) {
        throw new Error("El navegador bloqueo la ventana de pago. Habilita las ventanas emergentes e intenta nuevamente.");
      }
      tabRef.current = tab;
    } catch (err) {
      const message = getErrorMessage(err, "No se pudo iniciar el pago");
      setPaymentApproval({ status: 2, description: message });
      setShowModalStatusPayment(true);
      releaseClaimIfNeeded("Error al iniciar el pago con Evertec");
      releaseLock();
    }
  };

  // Evertec confirms asynchronously via a socket webhook broadcast to the machine's own room - the
  // exact same room this table session's ecommerce machine already uses (see Socket.config.jsx).
  useEffect(() => {
    if (!show) return undefined;
    const machine = safeParse(localStorage.getItem("machine"));
    const branchoffice = safeParse(localStorage.getItem("branchoffice"));
    if (!machine?.code || !branchoffice?.code) return undefined;

    const socket = Socket({ machine_code: machine.code, branchoffice_code: branchoffice.code });

    const handleEvertechPay = (data) => {
      if (!evertecRequestIdRef.current || data?.requestId !== evertecRequestIdRef.current) return;
      closeTab();

      const status = getGatewayStatus(data);
      if (status === "APPROVED") {
        const payment0 = Array.isArray(data?.payment) ? data.payment[0] : null;
        data.evertechPaymentReference = payment0?.reference;
        data.voucher = buildEvertecVoucher(data);
        finalizeCheckout({
          EMVStreamResponse: data,
          evertechPaymentReference: data.evertechPaymentReference,
        });
      } else {
        const message = getGatewayStatusMessage(data);
        setPaymentApproval({ status: 2, description: message });
        ErrorAlert("", message, "error");
        releaseClaimIfNeeded("Pago rechazado por Evertec");
        // Same auto-dismiss as Checkout.jsx's evertechpay handler. Also calls onClose directly (not
        // just the state setter) since this runs from inside the component, not from the Modal's own
        // onHide - without it the parent never learns to reset show/selectedTablePayment and the
        // customer would be stuck unable to retry until a full page reload.
        setTimeout(() => {
          setShowModalStatusPayment(false);
          releaseLock();
          onClose?.();
        }, 3000);
      }
    };

    socket.on("evertechpay", handleEvertechPay);
    return () => {
      socket.off("evertechpay", handleEvertechPay);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // ================= PowerTranz =================
  const cancelPowerTranz = (message = "Pago rechazado") => {
    setShowIframe(false);
    setHtmlData("");
    powertranzTxRef.current = null;
    setPaymentApproval({ status: 2, description: message });
    ErrorAlert("", message, "error");
    releaseClaimIfNeeded(message);
    // See the same note on handleEvertechPay's failure branch - onClose must be called directly here
    // too, so the parent resets and the customer can pick a payment method again.
    setTimeout(() => {
      setShowModalStatusPayment(false);
      releaseLock();
      onClose?.();
    }, 3000);
  };

  const renderPowerTranzIframe = (redirectData) => {
    const htmlTemplate = `
      <!DOCTYPE html>
      <html lang="es">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            html, body { margin:0; padding:0; box-sizing:border-box; width:100vw; min-height:100vh; background-color:transparent !important; overflow:hidden !important; }
            .bg-white { background-color: transparent !important; }
          </style>
        </head>
        <body>
          <div class="form-container">${redirectData}</div>
        </body>
      </html>
    `;
    setHtmlData(htmlTemplate);
    setShowIframe(true);
  };

  const payWithPowerTranz = async () => {
    const machine = safeParse(localStorage.getItem("machine"));
    const branchoffice = safeParse(localStorage.getItem("branchoffice"));
    if (!branchoffice?.id || !machine?.id) {
      setPaymentApproval({ status: 2, description: "No se encontro la sucursal o la maquina" });
      setShowModalStatusPayment(true);
      releaseLock();
      return;
    }

    setShowModalStatusPayment(true);
    setPaymentApproval({ status: 0, description: "Procesando pago..." });

    try {
      const orderNumber = await getOrderNumber();
      const identifier = orderNumber + "X" + generateRandomString(4);
      setOrderIdPowertranz(identifier);

      // Card fields are intentionally blank placeholders, same as the cart checkout - this flow is
      // 100% 3D Secure challenge based, never a direct card-capture form. Same staging base URL the
      // cart checkout itself hardcodes today (Checkout.jsx#powertranz).
      const response = await PowertranzFetchAPI.sale({
        branchoffice_id: branchoffice.id,
        branch_machine_id: machine.id,
        flow: "sale",
        TotalAmount: Number(getCurrentTotals().total) || 0,
        CurrencyCode: "840",
        ThreeDSecure: true,
        OrderIdentifier: identifier,
        Source: { CardPan: " ", CardExpiration: " ", CardCvv: " ", CardHolder: " " },
        BillingAddress: {
          FirstName: " ", LastName: " ", Line1: " ", Line2: " ", City: " ",
          State: " ", PostalCode: " ", CountryCode: " ", EmailAddress: customerEmail || " ", PhoneNumber: " ",
        },
        AddressMatch: false,
        ExtendedData: { MerchantResponseUrl: "" },
      }, "https://staging.ptranz.com", undefined);

      const dataNow = response?.data;
      if (!dataNow?.redirectHtml) {
        throw new Error("No se recibio el formulario 3D Secure de PowerTranz");
      }

      powertranzTxRef.current = { TransactionIdentifier: dataNow.txId, SpiToken: dataNow.spiToken };
      renderPowerTranzIframe(dataNow.redirectHtml);
    } catch (err) {
      cancelPowerTranz(getErrorMessage(err, "Sin respuesta 3DS"));
    }
  };

  const completePowerTranzSale = async () => {
    const tx = powertranzTxRef.current;
    if (!tx?.TransactionIdentifier) {
      cancelPowerTranz();
      return;
    }

    try {
      const response = await PowertranzFetchAPI.payment({
        SpiToken: tx.SpiToken,
        TransactionIdentifier: tx.TransactionIdentifier,
        AutoComplete: true,
      }, undefined);

      const approved = response?.data?.raw?.Approved === true || response?.data?.status === "PAID";
      if (!approved) {
        cancelPowerTranz();
        return;
      }

      setShowIframe(false);
      setHtmlData("");
      setPaymentApproval({ status: 0, description: "Registrando ..." });
      setTimeout(() => {
        finalizeCheckout({
          powertranz: response?.data,
          powertranz_spiToken: tx.SpiToken,
        });
      }, 1500);
    } catch (err) {
      cancelPowerTranz(getErrorMessage(err, "No se pudo completar el pago"));
    }
  };

  // PowerTranz's 3DS result arrives via a socket webhook scoped to this specific OrderIdentifier -
  // "PENDING" here means the 3DS challenge finished and the sale is ready to be completed (different
  // semantics from Evertec's PENDING, which means manual review - see the research report this was
  // ported from).
  useEffect(() => {
    if (!show || !orderIdPowertranz) return undefined;
    const branchoffice = safeParse(localStorage.getItem("branchoffice"));
    if (!branchoffice?.id) return undefined;

    const socket = Socket({ branchoffice_id: branchoffice.id, order_code: orderIdPowertranz });

    const handlePaymentUpdate = (data) => {
      if (data.status === "APPROVED" || data.status === "PAID") {
        completePowerTranzSale();
      } else if (data.status === "PENDING") {
        completePowerTranzSale();
      } else {
        cancelPowerTranz(data?.message || "Pago rechazado");
      }
    };

    socket.on("payment_update", handlePaymentUpdate);
    return () => {
      socket.off("payment_update", handlePaymentUpdate);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, orderIdPowertranz]);

  // ================= Yappy =================
  const requestYappyBtnOrder = async (btnYappy, phoneClientYappy) => {
    // Unlike Checkout.jsx's own ModalYappyBtn (pre-filled from the delivery/pickup contact form's
    // required phone field), the table flow has no contact-info step at all - ModalYappyBtn is
    // rendered here with phone="" and nothing else ever supplies a value, so the input is only ever
    // filled if the customer notices and types it themselves before tapping <btn-yappy>. Skipping it
    // used to reach the backend and come back as a bare 400 "Campos vacios" (controller.createYappyBtnOrder's
    // own validation) - this catches it client-side first with a message that actually says what's
    // wrong, and keeps the Yappy modal open so they can just type the number and retry instead of the
    // whole payment attempt getting torn down like every other Yappy failure below.
    // phoneClientYappy already went through ClearPhone (see ModalYappyBtn.jsx) - dashes/spaces/507
    // prefix stripped - so a valid Panama number is exactly 8 digits at this point. Anything else
    // (a customer who filled it in but with a landline, a typo, or a partial number) used to still
    // reach Yappy's API and come back as the exact same opaque 400 the empty case did.
    if (!phoneClientYappy || !/^\d{8}$/.test(phoneClientYappy)) {
      ErrorAlert("", "Ingresa un numero de telefono valido (8 digitos) para pagar con Yappy", "warning");
      return;
    }
    try {
      const machine = safeParse(localStorage.getItem("machine"));
      const orderNumber = await getOrderNumber();
      const totals = getCurrentTotals();
      // The real amount actually charged - unaffected by any of this, correct either way.
      const total = Number(totals.total) || 0;
      // totals.subtotal (calculateOrderTotals#sub_total_order on the backend) is already NET of any
      // per-line discount (see utils/calculateTotals.js#calculateTotalProductPrice - itemPrice =
      // amount * (price - unitDiscount)), while totals.discount reports that same amount again as
      // its own positive figure. Yappy's own payment-wc validation reconstructs
      // total = subtotal - discount + taxes, so forwarding the already-discounted subtotal alongside
      // a nonzero discount subtracts it a second time and the numbers stop reconciling - Yappy then
      // rejects the charge (this is the ecommerce mesa case: line discounts come from the "Pedir
      // descuento" feature, which doesn't exist on the regular non-table checkout, so it never showed
      // up there). Rather than try to reconstruct a gross subtotal Yappy might still disagree with,
      // just report the one number that has to be right - subtotal=total, discount/taxes=0 - so the
      // formula trivially holds no matter how the order got discounted.
      const response = await YappyFetchAPI.newYappyBtnOrder({
        params: [],
        transaction_code: orderNumber,
        description: "Smartpay - orden:" + orderNumber,
        phone: phoneClientYappy,
        discount: 0,
        taxes: 0,
        subtotal: total,
        total,
        machine_id: machine?.id,
      }, undefined);

      const yappyOrder = response?.data?.yappy_order?.body;
      if (!yappyOrder?.transactionId || !yappyOrder?.documentName || !yappyOrder?.token) {
        throw new Error("Yappy no devolvio los datos para iniciar el pago.");
      }

      // Stashes the transactionId (and whole-account: documentName/token) on the table's TempOrder
      // BEFORE handing off to the widget, so Yappy's own server-to-server IPN webhook
      // (smartpay-dashboard controller/yappy.js#getYappyBtnOrderNotification) can find this order and
      // finalize the payment itself if this tab never comes back after the customer approves in the
      // Yappy app - see attachYappyTrackingToClaim/attachYappyWholeAccountTracking on the backend.
      // Best-effort: this tab's own eventSuccess/eventPayment handler below (finalizeCheckout) is
      // still the primary path and works exactly as before even if this call fails - a failure here
      // only means the webhook fallback won't be able to find this specific attempt.
      try {
        if (isPartialMode) {
          if (paymentAttemptIdRef.current) {
            await TableSessionsFetchAPI.trackYappyClaim(paymentAttemptIdRef.current, {
              yappy_transaction_id: yappyOrder.transactionId,
              paymethod_id: payment?.payment_id,
              paymenthod_integration_id: getPaymentIntegrationId(payment),
            });
          }
        } else {
          await TableSessionsFetchAPI.trackYappyPayment({
            transaction_id: yappyOrder.transactionId,
            document_name: yappyOrder.documentName,
            token: yappyOrder.token,
            paymethod_id: payment?.payment_id,
            paymenthod_integration_id: getPaymentIntegrationId(payment),
            client_name: customerName || "",
            client_email: customerEmail || "",
          });
        }
      } catch (trackErr) {
        console.error("No se pudo registrar el seguimiento de Yappy para respaldo por backend", trackErr);
      }

      if (btnYappy && typeof btnYappy.eventPayment === "function") {
        btnYappy.eventPayment({
          transactionId: yappyOrder.transactionId,
          documentName: yappyOrder.documentName,
          token: yappyOrder.token,
        });
      }
    } catch (err) {
      const message = getErrorMessage(err, "No se pudo iniciar el pago con Yappy");
      setPaymentApproval({ status: 2, description: message });
      setShowModalStatusPayment(true);
      setShowYappyBtnModal(false);
      releaseClaimIfNeeded("Error al iniciar el pago con Yappy");
      // Same auto-dismiss as the other gateways' failure branches below - without it the customer
      // is stuck on the error screen forever instead of landing back on the table cart to retry.
      setTimeout(() => {
        setShowModalStatusPayment(false);
        releaseLock();
        onClose?.();
      }, 3000);
    }
  };

  const nextStepYappyTable = async (btnYappy, phoneClientYappy, event, detail) => {
    const status = getYappyEventStatus(event, detail);

    if (status === "CREATE") {
      await requestYappyBtnOrder(btnYappy, phoneClientYappy);
      return;
    }

    if (isYappyRejectedStatus(status)) {
      setShowModalStatusPayment(true);
      setShowYappyBtnModal(false);
      setPaymentApproval({ status: 2, description: "Pago rechazado por el usuario" });
      releaseClaimIfNeeded("Pago rechazado por el usuario (Yappy)");
      setTimeout(() => {
        setShowModalStatusPayment(false);
        releaseLock();
        onClose?.();
      }, 3000);
      return;
    }

    if (status.includes("EXPIRED")) {
      setShowModalStatusPayment(true);
      setShowYappyBtnModal(false);
      setPaymentApproval({ status: 2, description: "Pago expirado" });
      releaseClaimIfNeeded("Pago expirado (Yappy)");
      setTimeout(() => {
        setShowModalStatusPayment(false);
        releaseLock();
        onClose?.();
      }, 3000);
      return;
    }

    // Anything else here is treated as success, same as Checkout.jsx's nextStepYappy - the SDK's
    // eventSuccess is the only other event actually wired up on the <btn-yappy> element.
    setPaymentApproval({ status: 1, description: "Realizado" });
    setShowModalStatusPayment(true);
    setShowYappyBtnModal(false);
    setTimeout(async () => {
      await finalizeCheckout({ EMVStreamResponse: detail });
    }, 2000);
  };

  // ================= Pagar con POS =================
  // No online gateway to contact - the claim above (channel: "POS_PHYSICAL") already told the backend
  // to notify the waiter's POS in real time. This just shows the "esperando" state; the effect below
  // watches the reactive `order` prop for the claimed lines' payment_status flipping to PAID/UNPAID
  // once the waiter actually confirms or cancels the physical charge.
  const requestPosPayment = () => {
    setShowModalStatusPayment(true);
    setPaymentApproval({ status: 0, description: "Solicitud enviada al mesero. Espera la confirmacion del pago en el POS..." });
  };

  useEffect(() => {
    if (!show || !isPosPayment(payment) || !paymentAttemptIdRef.current) return;

    const claimedIds = new Set(lineUuids || []);
    const allLines = [...(order?.groups || []), ...(order?.combos || [])];
    const relevant = allLines.filter((line) => claimedIds.has(line.line_uuid));
    if (relevant.length === 0) return;

    const allPaid = relevant.every((line) => line.payment_status === "PAID");
    const anyUnpaid = relevant.some((line) => line.payment_status === "UNPAID");

    if (allPaid) {
      paymentAttemptIdRef.current = null;
      setPaymentApproval({ status: 1, description: "Pago confirmado por el mesero." });
      setTimeout(() => {
        const result = order;
        resetAndClose();
        onSuccess?.(result, { partial: true });
      }, 2000);
    } else if (anyUnpaid) {
      paymentAttemptIdRef.current = null;
      setPaymentApproval({ status: 2, description: "El pago fue cancelado en el POS. Elige otro metodo para continuar." });
      setTimeout(() => {
        setShowModalStatusPayment(false);
        releaseLock();
        onClose?.();
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const dispatchGateway = () => {
    if (isYappyPayment(payment)) {
      setShowYappyBtnModal(true);
    } else if (isEvertecPayment(payment)) {
      setShowModalStatusPayment(true);
      setPaymentApproval({ status: 0, description: "Procesando pago..." });
      payWithEvertec();
    } else if (isPowerTranzPayment(payment)) {
      payWithPowerTranz();
    } else if (isPosPayment(payment)) {
      requestPosPayment();
    } else if (isTilopayPayment(payment)) {
      // Tilopay solo opera en el checkout del ecommerce. Jamas debe llegar al ultimo else, que registra la cuenta sin cobrar.
      setShowModalStatusPayment(true);
      setPaymentApproval({ status: 2, description: "Este método de pago no está disponible para pagar la mesa." });
      releaseLock();
    } else {
      // Credit / bank transfer / anything without a real-time gateway: no online charge to run -
      // the physical confirmation (cash, transfer receipt, etc.) already happened at the table, so
      // just register the closing order with this method as the reference.
      finalizeCheckout({});
    }
  };

  // ================= Dispatch =================
  useEffect(() => {
    if (!show || startedRef.current || !payment) return;
    if (inFlightRef.current) return;

    startedRef.current = true;
    inFlightRef.current = true;

    if (!isPrepay) {
      dispatchGateway();
      return;
    }

    // Pre-pay: self-authorize (there is no waiter to do it) and fetch a server-verified total
    // BEFORE contacting any gateway - not just before the final checkout. Evertec/PowerTranz both
    // reach a payment.authorized-gated backend call (trackEvertechPayment / the sale itself) before
    // their gateway ever confirms a charge, so this has to run ahead of all three uniformly. See
    // beginPrepayPayment's own comment on the backend for the full reasoning.
    //
    // Partial payment mode (lineUuids given) never calls beginPrepayPayment/getPrepayOrderCalculation
    // at all - those lock the WHOLE order/session, which must never happen here (the account stays
    // open for more ordering/payment rounds). claimPrepayLines quotes AND locks only the selected
    // lines in one call - see services/tableSessionPayments.js#claimLinesForPayment on the backend.
    (async () => {
      try {
        if (isPartialMode) {
          const claim = await TableSessionsFetchAPI.claimPrepayLines(
            lineUuids,
            isPosPayment(payment) ? "POS_PHYSICAL" : undefined
          );
          paymentAttemptIdRef.current = claim?.payment_attempt_id || null;
          calculationIdRef.current = claim?.calculation_id || null;
          const nextTotals = {
            subtotal: claim?.subtotal,
            total: claim?.total,
            tax: claim?.tax,
            discount: claim?.discount,
          };
          verifiedTotalsRef.current = nextTotals;
          setVerifiedTotals(nextTotals);
        } else {
          await TableSessionsFetchAPI.beginPrepayPayment();
          const calculation = await TableSessionsFetchAPI.getPrepayOrderCalculation();
          calculationIdRef.current = calculation?.calculation_id || null;
          const nextTotals = {
            subtotal: calculation?.subtotal,
            total: calculation?.total,
            tax: calculation?.tax,
            discount: calculation?.discount,
          };
          verifiedTotalsRef.current = nextTotals;
          setVerifiedTotals(nextTotals);
        }
        dispatchGateway();
      } catch (err) {
        const message = getErrorMessage(err, "No se pudo iniciar el pago");
        setPaymentApproval({ status: 2, description: message });
        setShowModalStatusPayment(true);
        setTimeout(() => {
          setShowModalStatusPayment(false);
          releaseLock();
          onClose?.();
        }, 3000);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, payment]);

  useEffect(() => {
    if (!show) {
      startedRef.current = false;
      // Each payment attempt needs its own order number/calculation/claim - a customer can reopen
      // this component for a second partial payment (or a retry) within the same table visit, and
      // none of these should carry over from whatever the previous attempt used.
      orderNumberRef.current = null;
      calculationIdRef.current = null;
      paymentAttemptIdRef.current = null;
      evertecRequestIdRef.current = null;
      powertranzTxRef.current = null;
      verifiedTotalsRef.current = null;
      setVerifiedTotals(null);
    }
  }, [show]);

  if (!show) return null;

  return (
    <>
      <ModalYappyBtn
        showModal={showYappyBtnModal}
        setShowModal={(value) => {
          setShowYappyBtnModal(value);
          if (value === false) {
            releaseClaimIfNeeded("Cancelado por el usuario (Yappy)");
            releaseLock();
            onClose?.();
          }
        }}
        nextStepYappy={nextStepYappyTable}
        phone=""
      />

      <ModalStatusPayment
        showModalStatusPayment={showModalStatusPayment}
        setShowModalStatusPayment={(value) => {
          setShowModalStatusPayment(value);
          if (value === false && paymentApproval.status !== 1) {
            // Customer backed out while still waiting on the waiter's POS - release the claim right
            // away instead of leaving it locked until the backend's own claim TTL expires.
            if (isPosPayment(payment) && paymentApproval.status === 0) {
              releaseClaimIfNeeded("Cancelado por el cliente mientras esperaba el POS");
              releaseLock();
            }
            onClose?.();
          }
        }}
        paymentInfo={payment}
        paymentApproval={paymentApproval}
        invoiceSubtotal={Number(totals.subtotal) || 0}
        invoiceTotal={Number(totals.total) || 0}
        invoiceTotalDiscount={Number(totals.discount) || 0}
        invoiceTotalItbms={Number(totals.tax) || 0}
        invoiceTip={0}
        invoiceDeliveryFee={0}
      />

      {showIframe ? (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 2000,
            background: "rgba(0,0,0,0.6)", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}
        >
          <div style={{ background: "#fff", borderRadius: 12, padding: 16, width: "min(94vw, 480px)" }}>
            <iframe
              title="PowerTranz 3D Secure"
              srcDoc={htmlData}
              sandbox="allow-scripts allow-forms allow-same-origin"
              style={{ width: "100%", height: "60vh", border: 0 }}
            />
            <button
              type="button"
              className="btn btn-outline-danger w-100 mt-2"
              onClick={() => cancelPowerTranz("Pago cancelado por el usuario")}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
